// Shared "real name" validation used by both the signup form (client) and
// the /api/register endpoint (server) so the rules can't drift apart.
//
// Goal: reject obviously fake / junk names (digits, "asdf", single letters,
// symbols, URLs) without being so strict that we lock out legitimate people
// with apostrophes, hyphens, accents, or spaces in their names. This is a
// heuristic, not identity verification — it raises the floor, it doesn't
// prove the name is genuine.

// Letters across common Latin/extended ranges, plus spaces and the joiners
// that legitimately appear in names: hyphen, apostrophe (straight + curly),
// and period (for initials like "J.").
const ALLOWED_NAME_CHARS = /^[\p{L}\p{M}][\p{L}\p{M} '’.\-]*$/u;

// Reject the same character repeated 4+ times in a row ("aaaa", "zzzz") —
// a cheap catch for keyboard-mash names that otherwise pass the char check.
const REPEATED_RUN = /(.)\1{3,}/u;

export function isValidRealName(value: string | null | undefined): boolean {
  const name = String(value ?? "").trim();

  // Must contain at least two letters total (so "J" or "A." is rejected,
  // but "Jo" / "Li" / "Wu" pass).
  const letterCount = (name.match(/\p{L}/gu) ?? []).length;
  if (letterCount < 2) return false;

  if (name.length > 60) return false;
  if (!ALLOWED_NAME_CHARS.test(name)) return false;
  if (REPEATED_RUN.test(name)) return false;

  // No digits anywhere.
  if (/\d/u.test(name)) return false;

  return true;
}

// Human-readable reason, suitable as a form/API validation message.
export const REAL_NAME_MESSAGE =
  "Enter your real name (letters only — no numbers or symbols).";
